import React, { useEffect, useState } from 'react';
import {
  Breadcrumb,
  Button,
  ConfigProvider,
  Form,
  Input,
  Space,
  Select,
  Collapse,
  Switch,
} from 'antd';
import { HomeOutlined, SaveOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import Bread from 'modules/layoutModule/components/Bread/Bread';
import plPL from 'antd/lib/locale/pl_PL';
import 'moment/locale/pl';
import 'moment-timezone';
import FormCoverUpload from 'modules/galleryModules/components/FormCoverUpload';
import { FieldData } from 'rc-field-form/lib/interface';
import {
  createFertilizationRequest,
  getPlantsScheduleRequest,
} from 'modules/knowledgeModule/requests/knowledgeRequests';
import TextEditor from 'modules/layoutModule/components/TextEditor/TextEditor';
import copyToClipboard from 'helpers/copyToClipboard';

const { TextArea } = Input;
const { Panel } = Collapse;

const FertilizationAdd = (): JSX.Element => {
  const [isSaveButtonLoading, toggleButtonLoading] = useState(false);
  const [form] = Form.useForm();
  const [fileImage1, setFileImage1] = useState<string | null>(null);
  const [fileImage2, setFileImage2] = useState<string | null>(null);
  const [fileImage3, setFileImage3] = useState<string | null>(null);
  const [fileImage4, setFileImage4] = useState<string | null>(null);
  const [fields, setFields] = useState<FieldData[]>([]);
  const [plantsSchedule, setPlantsSchedule] = useState([]);

  const validateMessages = {
    required: 'Proszę uzupełnić pole ${label}!',
    types: {
      email: '${label} is not a valid email!',
      number: '${label} is not a valid number!',
    },
    number: {
      range: '${label} must be between ${min} and ${max}',
    },
  };

  const onContentEdit = (e: string, field: string) => {
    setFields([
      {
        name: [field],
        value: e,
      },
    ]);
  };

  useEffect(() => {
    getPlantsScheduleRequest().then((response) => {
      if (response.data) {
        const sections = response.data.map(
          (section: { name: string; id: number }) => {
            return {
              label: section.name,
              value: section.id,
            };
          }
        );
        sections.unshift({ label: '', value: '' });
        setPlantsSchedule(sections);
      }
    });
  }, []);

  const onSubmitForm = (values: any) => {
    toggleButtonLoading(true);
    createFertilizationRequest(values).then(() => toggleButtonLoading(false));
  };

  const updateCover = (
    e: string,
    field: string,
    coverMethod: (value: string) => void
  ) => {
    setFields([
      {
        name: [field],
        value: e,
      },
    ]);
    coverMethod(e);
  };

  return (
    <>
      <Bread>
        <>
          <Breadcrumb.Item>
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>Wiedza</Breadcrumb.Item>
          <Breadcrumb.Item>
            <Link to="/fertilization">Nawożenie upraw</Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <Link to="/fertilization/add">Dodaj</Link>
          </Breadcrumb.Item>
        </>
      </Bread>
      <div className="site-layout-background">
        <ConfigProvider locale={plPL}>
          <Form
            layout="vertical"
            validateMessages={validateMessages}
            onFinish={onSubmitForm}
            form={form}
            fields={fields}
          >
            <Form.Item
              name="fileImage1"
              label="Okładka"
              rules={[
                {
                  required: true,
                },
              ]}
            >
              <FormCoverUpload
                cover={fileImage1}
                setCover={(e) => updateCover(e, 'fileImage1', setFileImage1)}
                isNew
                field="fileImage1"
                number={1}
                hideTags
                originalFile
              />
            </Form.Item>
            <Form.Item
              name="fileImage2"
              label="Piktogram biały"
              rules={[
                {
                  required: true,
                },
              ]}
            >
              <FormCoverUpload
                cover={fileImage2}
                setCover={(e) => updateCover(e, 'fileImage2', setFileImage2)}
                isNew
                field="fileImage2"
                number={2}
                hideTags
                originalFile
              />
            </Form.Item>
            <Form.Item
              name="fileImage3"
              label="Piktogram zielony ciemny"
              rules={[
                {
                  required: true,
                },
              ]}
            >
              <FormCoverUpload
                cover={fileImage3}
                setCover={(e) => updateCover(e, 'fileImage3', setFileImage3)}
                isNew
                field="fileImage3"
                number={3}
                hideTags
                originalFile
              />
            </Form.Item>
            <Form.Item
              name="fileImage4"
              label="Piktogram zielony jasny"
              rules={[
                {
                  required: true,
                },
              ]}
            >
              <FormCoverUpload
                cover={fileImage4}
                setCover={(e) => updateCover(e, 'fileImage4', setFileImage4)}
                isNew
                field="fileImage4"
                number={4}
                hideTags
                originalFile
              />
            </Form.Item>
            <Form.Item
              name="name"
              label="Nazwa w sliderze"
              rules={[
                {
                  required: true,
                },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="name2"
              label="Nazwa tytułu wpisu"
              rules={[
                {
                  required: true,
                },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="name3"
              label="Nazwa nad menu"
              rules={[
                {
                  required: true,
                },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item name="plants_schedule" label="Połącz z harmonogramem">
              <Select
                showSearch
                placeholder="Wybierz harmonogram nawożenia"
                optionFilterProp="children"
                options={plantsSchedule}
                filterOption={(input, option) => {
                  // @ts-ignore
                  if (option?.label) {
                    // @ts-ignore
                    const label = option?.label as string;
                    return label.toLowerCase().includes(input.toLowerCase());
                  }
                  return false;
                }}
              />
            </Form.Item>
            <Form.Item name="name1" label="Nazwa przycisku w harmonogramie">
              <Input />
            </Form.Item>
            <Form.Item
              name="description"
              label="Podstawowe informacje"
              rules={[
                {
                  required: true,
                },
              ]}
            >
              {/* @ts-ignore */}
              <TextEditor
                onChange={(e) => onContentEdit(e, 'description')}
                disableGallery
              />
            </Form.Item>
            <Form.Item
              name="climatic_req"
              label="Wymagania klimatyczno-glebowe"
            >
              {/* @ts-ignore */}
              <TextEditor
                onChange={(e) => onContentEdit(e, 'climatic_req')}
                disableGallery
              />
            </Form.Item>
            <Form.Item name="nutritional_req" label="Wymagania pokarmowe">
              {/* @ts-ignore */}
              <TextEditor
                onChange={(e) => onContentEdit(e, 'nutritional_req')}
                disableGallery
              />
            </Form.Item>
            <Form.Item name="natural_fert" label="Nawożenie naturalne">
              {/* @ts-ignore */}
              <TextEditor
                onChange={(e) => onContentEdit(e, 'natural_fert')}
                disableGallery
              />
            </Form.Item>
            <Form.Item name="presowing_fert" label="Nawożenie przedsiewne">
              {/* @ts-ignore */}
              <TextEditor
                onChange={(e) => onContentEdit(e, 'presowing_fert')}
                disableGallery
              />
            </Form.Item>
            <Form.Item name="nitrogen_fert" label="Nawożenie azotem">
              {/* @ts-ignore */}
              <TextEditor
                onChange={(e) => onContentEdit(e, 'nitrogen_fert')}
                disableGallery
              />
            </Form.Item>
            <Form.Item name="sulfur_fert" label="Nawożenie siarką">
              {/* @ts-ignore */}
              <TextEditor
                onChange={(e) => onContentEdit(e, 'sulfur_fert')}
                disableGallery
              />
            </Form.Item>
            <Form.Item name="foliar_feed" label="Dokarmianie dolistne">
              {/* @ts-ignore */}
              <TextEditor
                onChange={(e) => onContentEdit(e, 'foliar_feed')}
                disableGallery
              />
            </Form.Item>
            <Form.Item name="quality" label="Wpływ nawożenia na jakość plonu">
              {/* @ts-ignore */}
              <TextEditor
                onChange={(e) => onContentEdit(e, 'quality')}
                disableGallery
              />
            </Form.Item>
            <Form.Item
              name="fert_value"
              label="Wartość nawozowa resztek pożniwnych"
            >
              {/* @ts-ignore */}
              <TextEditor
                onChange={(e) => onContentEdit(e, 'fert_value')}
                disableGallery
              />
            </Form.Item>
            <Form.Item
              name="sortOrder"
              label="Kolejność"
              rules={[
                {
                  required: true,
                },
                {
                  validator: async (_, value) => {
                    if (value) {
                      if (
                        (value.length > 5 ||
                          !/^[0-9]*\,?\.?[0-9]*$/.test(value)) &&
                        value.length > 0
                      ) {
                        return Promise.reject(
                          new Error('Proszę podać poprawną wartość!')
                        );
                      }
                    }
                  },
                },
              ]}
            >
              <Input
                style={{
                  width: '100px',
                }}
              />
            </Form.Item>
            <div className="tables-collapse">
              <Collapse defaultActiveKey={[]}>
                <Panel header="Tabelka 1" key="1" forceRender>
                  <Form.Item name="table1" label="Tabelka 1">
                    <TextArea rows={4} />
                  </Form.Item>
                  <Button
                    type="link"
                    onClick={() => copyToClipboard('TABELKA#1')}
                    style={{ padding: 0, margin: 0 }}
                  >
                    TABELKA#1
                  </Button>
                </Panel>
                <Panel header="Tabelka 2" key="2" forceRender>
                  <Form.Item name="table2" label="Tabelka 2">
                    <TextArea rows={4} />
                  </Form.Item>
                  <Button
                    type="link"
                    onClick={() => copyToClipboard('TABELKA#2')}
                    style={{ padding: 0, margin: 0 }}
                  >
                    TABELKA#2
                  </Button>
                </Panel>
                <Panel header="Tabelka 3" key="3" forceRender>
                  <Form.Item name="table3" label="Tabelka 3">
                    <TextArea rows={4} />
                  </Form.Item>
                  <Button
                    type="link"
                    onClick={() => copyToClipboard('TABELKA#3')}
                    style={{ padding: 0, margin: 0 }}
                  >
                    TABELKA#3
                  </Button>
                </Panel>
                <Panel header="Tabelka 4" key="4" forceRender>
                  <Form.Item name="table4" label="Tabelka 4">
                    <TextArea rows={4} />
                  </Form.Item>
                  <Button
                    type="link"
                    onClick={() => copyToClipboard('TABELKA#4')}
                    style={{ padding: 0, margin: 0 }}
                  >
                    TABELKA#4
                  </Button>
                </Panel>
                <Panel header="Tabelka 5" key="5" forceRender>
                  <Form.Item name="table5" label="Tabelka 5">
                    <TextArea rows={4} />
                  </Form.Item>
                  <Button
                    type="link"
                    onClick={() => copyToClipboard('TABELKA#5')}
                    style={{ padding: 0, margin: 0 }}
                  >
                    TABELKA#5
                  </Button>
                </Panel>
                <Panel header="Tabelka 6" key="6" forceRender>
                  <Form.Item name="table6" label="Tabelka 6">
                    <TextArea rows={4} />
                  </Form.Item>
                  <Button
                    type="link"
                    onClick={() => copyToClipboard('TABELKA#6')}
                    style={{ padding: 0, margin: 0 }}
                  >
                    TABELKA#6
                  </Button>
                </Panel>
                <Panel header="Tabelka 7" key="7" forceRender>
                  <Form.Item name="table7" label="Tabelka 7">
                    <TextArea rows={4} />
                  </Form.Item>
                  <Button
                    type="link"
                    onClick={() => copyToClipboard('TABELKA#7')}
                    style={{ padding: 0, margin: 0 }}
                  >
                    TABELKA#7
                  </Button>
                </Panel>
                <Panel header="Tabelka 8" key="8" forceRender>
                  <Form.Item name="table8" label="Tabelka 8">
                    <TextArea rows={4} />
                  </Form.Item>
                  <Button
                    type="link"
                    onClick={() => copyToClipboard('TABELKA#8')}
                    style={{ padding: 0, margin: 0 }}
                  >
                    TABELKA#8
                  </Button>
                </Panel>
                <Panel header="Tabelka 9" key="9" forceRender>
                  <Form.Item name="table9" label="Tabelka 9">
                    <TextArea rows={4} />
                  </Form.Item>
                  <Button
                    type="link"
                    onClick={() => copyToClipboard('TABELKA#9')}
                    style={{ padding: 0, margin: 0 }}
                  >
                    TABELKA#9
                  </Button>
                </Panel>
                <Panel header="Tabelka 10" key="10" forceRender>
                  <Form.Item name="table10" label="Tabelka 10">
                    <TextArea rows={4} />
                  </Form.Item>
                  <Button
                    type="link"
                    onClick={() => copyToClipboard('TABELKA#10')}
                    style={{ padding: 0, margin: 0 }}
                  >
                    TABELKA#10
                  </Button>
                </Panel>
              </Collapse>
            </div>
            <Form.Item
              name="isActive"
              label="Widoczność"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
            <Form.Item>
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SaveOutlined />}
                  loading={isSaveButtonLoading}
                >
                  Zapisz
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </ConfigProvider>
      </div>
    </>
  );
};

export default FertilizationAdd;
